import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './styles.module.css';
import { useHistory } from 'react-router-dom';
import { BasketBarContent } from 'services/content';
import { useSelector, useDispatch } from 'react-redux';
import { REQUEST_MEDICATION } from 'helpers/monitoringEvents';

import {
  useAddress,
  useDirectDeliveryRange,
  useExemption,
  useIsOffboardingPharmacy,
  useMonitoringEvents,
  useMedicationDeliveryInfo,
  useSelectedPharmacy,
} from 'hooks';
import { useInDeliveryRange } from 'hooks/useInDeliveryRange';
import * as Types from 'helpers/types';
import useODSCode from 'hooks/useODSCode';
import { Spinner } from 'components';
import {
  isUserRowlandsInStore,
  isFirstOrder,
  isUserMarketingPatient,
  wasLastOrderToDsp,
  getBasketMedicationName,
  allMedicationBasketIsContraceptive,
  getBasketMedication
} from 'helpers/helperFunctions';

const BasketBar = ({ count }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    medication: { medication },
    orderHistory,
    paymentJourney: { basket, pharmaciesInRange },
  } = useSelector((state) => state);
  const [, loading, registeredAddress] = useAddress();
  const getDirectDeliveryRange = useDirectDeliveryRange();
  const [getExemption] = useExemption();
  const getInRange = useInDeliveryRange();
  const [offboardingData, , getIsOffboarding] = useIsOffboardingPharmacy();
  const [medicationInfoLoading, getMedicationInfo] = useMedicationDeliveryInfo();
  const [postLog] = useMonitoringEvents();
  const getFromODSCode = useODSCode();
  const [selectedPharmacy] = useSelectedPharmacy();

  useEffect(() => {
    !orderHistory.loading && getIsOffboarding(orderHistory?.orders[0]?.fulfillment?.pharmacy_ods_code || null);
  }, [orderHistory]);

  useEffect(() => {
    const getPharmaciesInRange = async () => {
      const response = await getDirectDeliveryRange(registeredAddress.postcode);
      if (response) dispatch({ type: Types.PHARMACIES_IN_RANGE_SET, payload: response });
    };
    !loading && getPharmaciesInRange();
  }, [loading, registeredAddress]);
  
  const startOrder = async () => {
    postLog(REQUEST_MEDICATION);
    dispatch({ type: Types.START_PAYMENT_JOURNEY });
    dispatch({ type: Types.MANUAL_ORDERS_CLEAR });
    dispatch({ type: Types.CLEAR_PAYMENT_AND_EXEMPTION });

    if (isUserRowlandsInStore(selectedPharmacy)) {
      if (isFirstOrder(orderHistory)) {
        history.push('/pharmacy/dashboard/find-pharmacy');
        return;
      } else {
        const lastOrderFulfillmentMethod = orderHistory?.orders[0].fulfillment.delivery_type;
        if (lastOrderFulfillmentMethod === 'collectFromPharmacy') {
          const address = {
            ...orderHistory.orders[0]?.fulfillment.address,
            ods_code: orderHistory.orders[0]?.fulfillment.pharmacy_ods_code,
            name: orderHistory.orders[0]?.fulfillment.pharmacy_name,
          };
          dispatch({ type: Types.DELIVERY_METHOD_SET, payload: 'collectFromPharmacy' });
          dispatch({ type: Types.ADDRESS_SET, payload: address });
          handleMedicationInfoNavigation('/pharmacy/dashboard/basket');
          return;
        } else {
          history.push('/pharmacy/dashboard/find-pharmacy');
          return;
        }
      }
    }

    try {
      if (!isUserMarketingPatient(selectedPharmacy)) {
        if (wasLastOrderToDsp(orderHistory)) {
          const pharmacyDetails = await getFromODSCode(selectedPharmacy.ods_code);
          const address = {
            ...pharmacyDetails.data.address,
            ods_code: selectedPharmacy.ods_code,
            name: pharmacyDetails.data.name,
          };
          dispatch({ type: Types.DELIVERY_METHOD_SET, payload: 'collectFromPharmacy' });
          dispatch({ type: Types.ADDRESS_SET, payload: address });
          await handleNavigate();
          return;
        }
        if (selectedPharmacy?.delivery_available && selectedPharmacy?.delivery_enabled) {
          const isInRange = await getInRange(selectedPharmacy?.ods_code, registeredAddress.postcode);
          dispatch({ type: Types.GP_REGISTERED_IN_RANGE_SET, payload: isInRange.data });
          if (isInRange.data) {
            dispatch({
              type: Types.ADDRESS_SET,
              payload: { ...registeredAddress, ods_code: selectedPharmacy.ods_code },
            });
            history.push('/pharmacy/dashboard/delivery-options');
            return;
          }
        }
        const pharmacyDetails = await getFromODSCode(selectedPharmacy.ods_code);
        const addressPayload = {
          ...pharmacyDetails.data.address,
          ods_code: selectedPharmacy.ods_code,
          name: pharmacyDetails.data.name,
        };
        dispatch({ type: Types.DELIVERY_METHOD_SET, payload: 'collectFromPharmacy' });
        dispatch({ type: Types.ADDRESS_SET, payload: addressPayload });
        await handleNavigate();
        return;
      }
    } catch (e) {
      if (e.response && e.response.data.readable_error_message) {
        dispatch({ type: Types.SET_ERROR_MESSAGE, payload: e.response.data.readable_error_message });
      }
    }

    if (wasLastOrderToDsp(orderHistory)) {
      const address = {
        ...orderHistory.orders[0]?.fulfillment.address,
        ods_code: orderHistory.orders[0]?.fulfillment.pharmacy_ods_code,
        name: orderHistory.orders[0]?.fulfillment.pharmacy_name,
      };
      dispatch({ type: Types.ADDRESS_SET, payload: address });
      await handleHomeDeliveryNavigate();
    } else if (
      orderHistory.orders[0]?.fulfillment?.delivery_type === 'collectFromPharmacy' &&
      pharmaciesInRange?.length === 0
    ) {
      const address = {
        ...orderHistory.orders[0]?.fulfillment.address,
        ods_code: orderHistory.orders[0]?.fulfillment.pharmacy_ods_code,
        name: orderHistory.orders[0]?.fulfillment.pharmacy_name,
      };
      dispatch({ type: Types.DELIVERY_METHOD_SET, payload: 'collectFromPharmacy' });
      dispatch({ type: Types.ADDRESS_SET, payload: address });
      await handleNavigate();
    } else {
      history.push('/pharmacy/dashboard/delivery-options');
    }
  };

  const handleHomeDeliveryNavigate = async () => {
    dispatch({ type: Types.DELIVERY_METHOD_SET, payload: 'homeDelivery' });
    const exemption = await getExemption();
    if (exemption?.exemption_type) {
      dispatch({
        type: Types.ADD_EXEMPTION,
        payload: {
          type: { option: exemption?.code || "", text: exemption?.name || ""},
          cert: {
            number: exemption?.number || "",
            expiry: exemption?.expiry_date || "",
          },
          ni_number: exemption?.ni_number || "",
        },
      });
      await handleMedicationInfoNavigation('/pharmacy/dashboard/basket');
    } else {
      if (allMedicationBasketIsContraceptive(getBasketMedication(basket, medication))){
        await handleMedicationInfoNavigation('/pharmacy/dashboard/basket');
      } else {
        await handleMedicationInfoNavigation('/pharmacy/dashboard/exemption');
      }
    }
  }

  const handleNavigate = async () => {
    if (offboardingData.showFulfillmentMessage) {
      history.push('/pharmacy/dashboard/delivery-options');
    } else {
      history.push('/pharmacy/dashboard/basket');
    }
  };

  if (loading || medicationInfoLoading) {
    return <Spinner />;
  }

  const handleMedicationInfoNavigation = async (location) => {
    try {
      const medicationDeliveryInfo = await getMedicationInfo(getBasketMedicationName(basket, medication));
      if (Object.values(medicationDeliveryInfo).some(x => x)) {
        history.push('/pharmacy/dashboard/delivery-options')
      } else {
        history.push(location);
      }
    } catch (e) {
      history.push('/pharmacy/dashboard/delivery-options');
    }
  }

  return (
    <div className={`${styles.basketBar} ${count && styles.basketBar__active}`}>
      <div className="coop-l-container">
        <div className={styles.basketBarinner}>
          <div className={styles.countHolder}>
            <div id="request-medication-count" className={styles.basketCount}>
              {count}
            </div>
            <p className={`${styles.countText} ${count && styles.countText__active}`}>{BasketBarContent.countText}</p>
          </div>
          <button
            id="request-medication-button"
            data-testid="request-medication-button"
            disabled={!count || loading || orderHistory.loading}
            onClick={() => startOrder()}
            className={`coop-btn coop-btn--primary ${styles.basketButton} ${count && styles.basketButton__active}`}>
            <div>{BasketBarContent.buttonText}</div>
          </button>
        </div>
      </div>
    </div>
  );
};

BasketBar.propTypes = {
  count: PropTypes.number.isRequired,
};

export default BasketBar;
